import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { ProjectAvatar } from "@/components/ui/project-avatar";
import { ContextMenu, ContextMenuTrigger } from "@/components/ui/context-menu";
import { ProjectContextMenuContent } from "./project-context-menu-content";

/** Project-only navigation for the Browse tab. */

interface BrowseProject {
  id: string;
  name: string;
  color: string;
  isHome?: boolean;
  isPinned?: boolean;
  /** Retained in the data contract while spaces remain an internal concept. */
  expanded?: boolean;
  spaces: BrowseSpace[];
}

interface BrowseSpace {
  id: string;
  name: string;
  isPinned?: boolean;
  active?: boolean;
}

interface BrowseTreeProps {
  projects: BrowseProject[];
  onSelectProject?: (projectId: string) => void;
  onSelectSpace?: (projectId: string, spaceId: string) => void;
  onToggleProject?: (projectId: string) => void;
  onNewProject?: () => void;
  onRenameProject?: (projectId: string, name: string) => void;
  onSetColorProject?: (projectId: string, color: string) => void;
  onPinProject?: (projectId: string) => void;
  onProjectSettings?: (projectId: string) => void;
  onNewSpace?: (projectId: string) => void;
  onDeleteProject?: (projectId: string) => void;
  onRenameSpace?: (projectId: string, spaceId: string, name: string) => void;
  onSetColorSpace?: (projectId: string, spaceId: string, color: string) => void;
  onPinSpace?: (projectId: string, spaceId: string) => void;
  onOrganizeFrames?: (projectId: string, spaceId: string) => void;
  onMoveSpace?: (
    fromProjectId: string,
    spaceId: string,
    toProjectId: string,
  ) => void;
  onDeleteSpace?: (projectId: string, spaceId: string) => void;
}

function sortPinnedFirst<T extends { isPinned?: boolean }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return 0;
  });
}

interface ProjectRowProps {
  project: BrowseProject;
  onSelect?: (projectId: string) => void;
  onRename?: (projectId: string, name: string) => void;
  onSetColor?: (projectId: string, color: string) => void;
  onPin?: (projectId: string) => void;
  onSettings?: (projectId: string) => void;
  onDelete?: (projectId: string) => void;
}

function ProjectRow({
  project,
  onSelect,
  onRename,
  onSetColor,
  onPin,
  onSettings,
  onDelete,
}: ProjectRowProps) {
  const active = project.spaces.some((space) => space.active);

  return (
    <div data-testid={`browse-tree-project-${project.id}`}>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "group flex w-full items-center gap-appkit-xs rounded-control-sm px-appkit-xs py-appkit-2xs text-left text-appkit-small font-medium transition-colors",
              active
                ? "bg-glass-fill-heavy text-primary"
                : "text-secondary hover:bg-glass-fill-heavy hover:text-primary",
            )}
            onClick={() => onSelect?.(project.id)}
            data-testid={`browse-tree-project-${project.id}-select`}
            role="treeitem"
            aria-current={active ? "page" : undefined}
          >
            <ProjectAvatar
              name={project.name}
              color={project.color}
              isHome={project.isHome}
              size="sm"
              data-testid={`browse-tree-project-${project.id}-avatar`}
            />
            <span className="flex-1 truncate">{project.name}</span>
            {project.isPinned && (
              <Icons.Pin
                className="h-appkit-xs w-appkit-xs shrink-0 text-muted opacity-60"
                aria-label="Pinned"
              />
            )}
          </button>
        </ContextMenuTrigger>
        <ProjectContextMenuContent
          project={project}
          onRename={(name) => onRename?.(project.id, name)}
          onSetColor={onSetColor}
          onPin={onPin}
          onSettings={onSettings}
          onDelete={onDelete}
        />
      </ContextMenu>
    </div>
  );
}

export function BrowseTree({
  projects,
  onSelectProject,
  onNewProject,
  onRenameProject,
  onSetColorProject,
  onPinProject,
  onProjectSettings,
  onDeleteProject,
}: BrowseTreeProps) {
  const sortedProjects = sortPinnedFirst(projects);

  return (
    <div
      className="flex flex-col"
      data-testid="browse-tree"
      role="tree"
      aria-label="Projects"
    >
      <div className="flex flex-col gap-[calc(var(--appkit-space-2xs)/2)] px-appkit-2xs py-appkit-2xs">
        {sortedProjects.map((project) => (
          <ProjectRow
            key={project.id}
            project={project}
            onSelect={onSelectProject}
            onRename={onRenameProject}
            onSetColor={onSetColorProject}
            onPin={onPinProject}
            onSettings={onProjectSettings}
            onDelete={onDeleteProject}
          />
        ))}

        {sortedProjects.length === 0 && (
          <p
            className="px-appkit-xs py-appkit-sm text-center text-appkit-mini text-muted"
            data-testid="browse-tree-empty"
          >
            No projects yet
          </p>
        )}
      </div>

      <div className="border-t border-glass-stroke-subtle px-appkit-xs py-appkit-2xs">
        <button
          type="button"
          className="flex w-full items-center gap-appkit-2xs rounded-control-sm px-appkit-2xs py-appkit-2xs text-appkit-small text-muted transition-colors hover:text-secondary"
          onClick={onNewProject}
          data-testid="browse-tree-new-project"
          aria-label="Create new project"
        >
          <Icons.Plus className="h-appkit-sm w-appkit-sm shrink-0" />
          New project
        </button>
      </div>
    </div>
  );
}

export type { BrowseTreeProps, BrowseProject, BrowseSpace };
